import React, { PropsWithChildren, Ref } from 'react';
import theme from './ResidentialListItemBuyerSection.module.scss';
import { TranslatableText, BuyerInfo, InvitationStatus } from '@/types';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { DateDistanceDisplay } from '@/components/localization';
import { StackedIcons, UserIcon } from '@/components/Icons';
import { faUserCheck } from '@fortawesome/pro-light-svg-icons';

interface ResidentialListItemBuyerSectionProps extends PropsWithChildren {
    buyerInfo: BuyerInfo;
    iconsRef?: Ref<HTMLDivElement>;
}

const ResidentialListItemBuyerSection: React.FC<ResidentialListItemBuyerSectionProps> = ({
    buyerInfo,
    children,
    iconsRef,
}) => {
    const buyers = buyerInfo.users;
    let name: TranslatableText = <Trans id="Available.Single">Available</Trans>;
    if (buyers.length > 1) name = buyers.map((b) => b.firstName).join(', ');
    else if (buyers.length === 1)
        name = [buyers[0].salutation, buyers[0].title, buyers[0].firstName, buyers[0].lastName].filter(Boolean).join(' ');

    const renderExtraInfo = () => {
        if (buyers.length === 0) return null;

        if (buyerInfo.lastVisitDate)
            return (
                <Trans>
                    Active: <DateDistanceDisplay value={buyerInfo.lastVisitDate} />
                </Trans>
            );

        const inviteStatuses = [...new Set(buyerInfo.users.map((u) => u.inviteStatus))].map((status) => {
            return {
                status,
                count: buyerInfo.users.filter((u) => u.inviteStatus === status).length,
            };
        });
        const getStatusTranslation = (status: InvitationStatus) => {
            switch (status) {
                case InvitationStatus.Pending:
                    return t`not invited`;
                case InvitationStatus.Sent:
                    return t`invited`;
                case InvitationStatus.Registered:
                    return t`accepted`;
                case InvitationStatus.Failed:
                    return t`failed`;
                case InvitationStatus.Canceled:
                    return t`canceled`;
                default:
                    return status;
            }
        };

        const statusTexts = inviteStatuses.map(({ count, status }) => `${count} ${getStatusTranslation(status)}`);

        return (
            <>
                {buyerInfo.users.length === 1 ? <Trans>Invite</Trans> : <Trans>Invites</Trans>}: {statusTexts.join(', ')}
            </>
        );
    };
    return (
        <div className="flex">
            {buyers.length > 0 ? (
                <StackedIcons ref={iconsRef}>
                    {buyers.map((b) => (
                        <UserIcon
                            key={b.userId + '_' + b.email}
                            emptyIcon={b.invitedDate && !b.acceptedDate ? faUserCheck : undefined}
                            className={theme.userIcon}
                            sizeInPx={30}
                            userId={b.userId}
                        />
                    ))}
                </StackedIcons>
            ) : (
                <UserIcon className={theme.userIcon} sizeInPx={30} emptyIcon={faUserCheck} ref={iconsRef} />
            )}
            <div className={theme.textSection}>
                {name}
                <span className={theme.infoText}>{renderExtraInfo()}</span>
                {children}
            </div>
        </div>
    );
};

export default ResidentialListItemBuyerSection;
